/* Settings layout (MAR-372) — the layout a settings panel needs that the design system had no
   component for, added for Authority. Appended to the library through
   `export * from './Settings'` in ./index.jsx. Pure layout: no colour, no new surfaces. */
import * as React from 'react';

const { useRef, useState, useEffect } = React;

const cx = (...a) => a.filter(Boolean).join(' ');

/** Vertical rhythm between the blocks of a panel (cards, alerts, fields). */
function Stack({ children, gap = 'md', as: Tag = 'div', className = '', ...rest }) {
  return <Tag {...rest} className={cx('mv-stack', gap !== 'md' && `mv-stack--${gap}`, className)}>{children}</Tag>;
}

/** A wrapping row of controls — a field and its button, a status and its action. Pass
 *  `grow`: every Field (and SettingRow) in the row then takes the spare width and every
 *  Button keeps its own (`.ms-addrow` / `.ms-inline` in migration.md — a Field and its one
 *  action). Push what follows to the far end with `<Spacer />`. `wrap={false}` keeps the row
 *  on one line (an avatar and its name in a table cell). */
function Inline({ children, align = 'center', justify = 'start', grow = false, wrap = true, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-inline', align === 'end' && 'mv-inline--end', grow && 'mv-inline--grow', !wrap && 'mv-inline--nowrap',
    justify === 'between' && 'mv-inline--between', justify === 'end' && 'mv-inline--right', className)}>{children}</div>;
}

/** Takes the spare width of a row, pushing what follows it to the far end — in an Inline,
 *  a CardFooter or a Drawer footer. */
function Spacer() {
  return <span className="mv-spring" aria-hidden="true" />;
}

/** @deprecated Use `<Inline align="end" grow>` — FieldRow is that row, kept as an alias. */
function FieldRow(props) {
  return <Inline align="end" grow {...props} />;
}

/** A two-up grid of fields that collapses to one column on a phone. */
function FieldGrid({ children, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-fieldgrid', className)}>{children}</div>;
}

/** One setting as a line: its name, what it means, and its state or control on the right.
 *  Consecutive rows are ruled. Use Toggle's own label row for a switch. */
function SettingRow({ title, description, aside, below, align = 'center', children, className = '', ...rest }) {
  return (
    <div {...rest} className={cx('mv-setrow', align === 'top' && 'mv-setrow--top', below && 'mv-setrow--below', className)}>
      <div className="mv-setrow-text">
        {title && <div className="mv-setrow-title">{title}</div>}
        {description && <div className="mv-setrow-desc">{description}</div>}
        {children}
      </div>
      {aside && <div className="mv-setrow-aside">{aside}</div>}
      {below && <div className="mv-setrow-below">{below}</div>}
    </div>
  );
}

/** Preformatted text to read or copy (an embed snippet). */
function CodeBlock({ children, className = '', ...rest }) {
  return <pre {...rest} className={cx('mv-codeblock', className)}><code>{children}</code></pre>;
}

/* ---------------- SectionNav (MAR-385) ----------------
   A page's own sections, one shown at a time: a vertical list of buttons beside the content,
   each with an optional trailing hint (a status word, a count, a Badge). The current one carries
   `aria-current="page"` — every section is deep-linkable, so it is a location, not a tab. Arrow
   keys (and Home / End) move focus along the list; Enter or Space opens the focused section.
   Below a 760px container it wraps into a horizontal row of pills, so it never takes a column
   the content needs. Generic on purpose: Limits uses it first, Store and Brand next. */
function SectionNav({ items = [], value, onChange, label, className = '' }) {
  const refs = useRef([]);
  const onKey = (e, i) => {
    const n = items.length; let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % n;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next != null) { e.preventDefault(); if (refs.current[next]) refs.current[next].focus(); }
  };
  return (
    <nav aria-label={label} className={cx('mv-secnav', className)}>
      <ul className="mv-secnav-list">
        {items.map((it, i) => {
          const on = it.id === value;
          const hasHint = it.hint != null && it.hint !== false && it.hint !== '';
          return (
            <li key={it.id} className="mv-secnav-li">
              <button ref={(el) => (refs.current[i] = el)} type="button" aria-current={on ? 'page' : undefined}
                className={cx('mv-secnav-item', on && 'mv-secnav-item--on')}
                onClick={() => { if (!on && onChange) onChange(it.id); }} onKeyDown={(e) => onKey(e, i)}>
                <span className="mv-secnav-label">{it.label}</span>
                {hasHint && <>{' '}<span className="mv-secnav-hint">{it.hint}</span></>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ---------------- SectionLayout (MAR-385) ----------------
   The frame a SectionNav sits in: nav · the one open section · an optional aside ("At a
   glance"). It is its own inline-size container, so it answers to the column it is given — beside
   the Ask dock that is often under 760px — never the viewport: three columns from 1040px, the
   aside under the section from 760px, one column (nav as a wrapping row) below that.
   `asideWidth="wide"` (MAR-400) is for an aside that holds a document preview: in the
   three-column band the aside takes what the section can spare above 480px, from 280px up to
   420px, instead of stopping at 280px. */
function SectionLayout({ nav, aside, asideLabel, asideWidth = 'default', children, className = '', ...rest }) {
  return (
    <div {...rest} className={cx('mv-seclayout', className)}>
      <div className={cx('mv-seclayout-grid', !aside && 'mv-seclayout-grid--noaside', aside && asideWidth === 'wide' && 'mv-seclayout-grid--wide-aside')}>
        {nav && <div className="mv-seclayout-nav">{nav}</div>}
        <div className="mv-seclayout-main">{children}</div>
        {aside && <aside className="mv-seclayout-aside" aria-label={asideLabel}>{aside}</aside>}
      </div>
    </div>
  );
}

/* ---------------- SectionPanel + useVisitedSections (MAR-386) ----------------
   What sits in SectionLayout's main column: one SectionPanel per section. A section is mounted
   the first time it is opened and then KEPT mounted, hidden, while another is open — so a draft
   typed into one section survives a trip to another and back, and a section's own reads run once
   rather than on every return. A hidden panel carries `hidden` (display:none: out of the tab
   order and the accessibility tree, so nothing in it is focusable or announced) and `inert`.

   `resetKey` is what the sections' data belongs to — the workspace id. When it changes, every
   section except the open one is dropped in that same render (scoped at the read, never in an
   effect), so a hidden section can neither hold the previous store's data nor read again for the
   new one until it is opened. Never-opened sections are not mounted at all: a page with five
   sections makes the reads of the ones the merchant actually visits. */
function useVisitedSections(value, resetKey) {
  const [held, setHeld] = useState(() => ({ key: resetKey, ids: [value] }));
  const ids = held.key === resetKey ? held.ids : [];
  const known = ids.includes(value);
  useEffect(() => {
    if (known) return;
    setHeld((h) => (h.key === resetKey
      ? (h.ids.includes(value) ? h : { key: resetKey, ids: [...h.ids, value] })
      : { key: resetKey, ids: [value] }));
  }, [known, resetKey, value]);
  return new Set(known ? ids : [...ids, value]);
}

function SectionPanel({ id, value, visited, children, className = '', ...rest }) {
  const open = id === value;
  if (!open && !(visited && visited.has(id))) return null;
  return (
    <div {...rest} className={cx('mv-secpanel', className)} data-section={id}
      hidden={!open} inert={open ? undefined : ''}>
      {children}
    </div>
  );
}

export { Stack, Inline, Spacer, FieldRow, FieldGrid, SettingRow, CodeBlock, SectionNav, SectionLayout, SectionPanel, useVisitedSections };
